import React, { useState, useEffect, useCallback } from 'react';

import { IconButton } from '@material-ui/core';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useDispatch, useSelector } from 'react-redux';
import Select, { components } from 'react-select';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { StyledTypography, StyledTooltip } from 'styles/common';

import { TRACKING_OPTIONS } from 'constants/ProjectConstants';
import { routes } from 'constants/routes';

import { getMetricName, unlinkMetric } from 'services/goalTracking';
import { getAuthDetails } from 'services/plugins';
import { showSuccessSnackbar, showErrorSnackbar } from 'services/snackbar';
import { refreshMetric } from 'services/task';

import PluginImage from './pluginImage';

const { Option } = components;

const SelectWrap = styled.div``;

const IconOption = (props) => (
  <Option {...props}>
    <PluginImage icon type={props.data.value} />
    {props.data.label}
  </Option>
);

const customStyles = {
  control: (base, { selectProps: { isHovered } }) => ({
    ...base,
    width: '100%',
    border: isHovered ? '1px solid hsl(0, 0%, 80%)' : '1px solid transparent',
    cursor: 'pointer',
  }),
  indicatorsContainer: (base, { selectProps: { isHovered } }) => ({
    ...base,
    visibility: isHovered ? 'visible' : 'hidden',
  }),
  menu: (provided, state) => ({
    ...provided,
    marginTop: 0,
    marginBottom: 0,
  }),
};

const TrackingField = ({ taskDetails, updateTask, toggleShowTrackingDetails, getTaskData = null }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [currentConnection, setCurrentConnection] = useState(null);
  const [metricName, setMetricName] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const employeeObj = useSelector(selectLoggedInEmployeeData);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  useEffect(() => {
    if (taskDetails.metricId) {
      setLoading(true);
      getMetricName({ taskId: taskDetails.id })
        .then((res) => setMetricName(res))
        .catch((err) => {
          dispatch(showErrorSnackbar(err.message || 'Sorry, unable to fetch the metric name'));
        })
        .finally(() => setLoading(false));
    }
  }, [taskDetails]);

  const handleChange = (selected) => {
    setLoading(true);
    getAuthDetails({ thirdPartyType: selected.value })
      .then((res) => {
        setCurrentConnection(res);
        if (res) {
          toggleShowTrackingDetails(true, selected);
        } else {
          setSelectedOption(selected);
        }
      })
      .catch((err) => {
        setSelectedOption(selected);
        dispatch(showErrorSnackbar('Sorry, unable to fetch the connections'));
      })
      .finally(() => setLoading(false));
  };

  const handleClick = () => {
    const selected = TRACKING_OPTIONS.filter((o) => o.value === taskDetails.trackedByPlugin)[0];
    toggleShowTrackingDetails(true, selected);
  };

  const handleUnlinkClick = () => {
    setLoading(true);
    unlinkMetric({ taskId: taskDetails.id })
      .then((res) => {
        dispatch(showSuccessSnackbar(res.message));
        getTaskData();
      })
      .catch((err) => dispatch(showErrorSnackbar('Something went wrong. Please try again later.')))
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRefreshClick = () => {
    setLoading(true);
    refreshMetric({ goalId: taskDetails.id })
      .then((res) => {
        dispatch(showSuccessSnackbar(res.message));
        getTaskData();
      })
      .catch((err) =>
        dispatch(showErrorSnackbar('Could not refresh! Try again later or check integration if the problem persists.'))
      )
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <FieldContainer>
      {taskDetails.trackedByPlugin ? (
        <>
          <Container onClick={handleClick}>
            <PluginImage icon type={taskDetails.trackedByPlugin} />
            <StyledTypography variant="body2" color="textSecondary" display="inline">
              {metricName || '-'}
            </StyledTypography>
          </Container>
          {getTaskData && (
            <>
              <StyledTooltip title="Un-link Metric">
                <StyledIconButton onClick={handleUnlinkClick} disabled={loading}>
                  <LinkOffIcon />
                </StyledIconButton>
              </StyledTooltip>
              <StyledTooltip title="Refresh Metric">
                <StyledIconButton onClick={handleRefreshClick} disabled={loading}>
                  <RefreshIcon />
                </StyledIconButton>
              </StyledTooltip>
            </>
          )}
        </>
      ) : (
        <SelectWrap onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <StyledSelect
            menuShouldScrollIntoView={false}
            options={TRACKING_OPTIONS}
            components={{ Option: IconOption }}
            onChange={handleChange}
            placeholder="Automatically track metric via…"
            isDisabled={loading}
            isHovered={isHovered}
            styles={customStyles}
          />

          {selectedOption &&
            !currentConnection &&
            !loading &&
            (employeeObj.userRole === 'ADMIN' ? (
              <NoticeText>
                <StyledTypography variant="paragraph" weight={600} color="textSecondary">
                  No active {selectedOption.value.toProperCase()} account.{' '}
                </StyledTypography>
                <Link onClick={() => window.open(routes.settings.plugins[selectedOption.value.toLowerCase()])}>
                  Link a new one.
                </Link>
              </NoticeText>
            ) : (
              <NoticeText>
                Please request your Mesh admin to integrate with company’s {selectedOption.value.toProperCase()}{' '}
                account.
              </NoticeText>
            ))}
        </SelectWrap>
      )}
    </FieldContainer>
  );
};

const FieldContainer = styled.div`
  margin-bottom: 1rem;
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin-left: 11px;
    padding-bottom: 5px;
    min-width: 220px;
  }
`;

const Container = styled.div`
  display: inline-block;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 0.38rem 1rem 0.38rem 1rem;
  margin-left: -8px;
  color: #333;
  cursor: pointer;
  margin-right: 0.75rem;
  max-width: calc(100% - 8rem);
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    background: #f7f7f7;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  // margin-left: -8px;
  font-size: 0.8rem;
`;

const StyledIconButton = styled(IconButton)`
  padding: 6px;
  margin-left: 0.25rem;
  svg {
    font-size: 18px;
  }
`;

const NoticeText = styled.span`
  font-size: 0.8rem;
  margin-top: 6px;
  display: inline-block;
  margin-left: -6px;
  font-family: ${(props) => props.theme.typography.fontFamily};
`;

const Link = styled.a`
  cursor: pointer;
  color: #0992f0;
  text-decoration: none;
  font-size: 0.8rem;
  &:hover {
    text-decoration: underline;
  }
`;

export default TrackingField;
